import { getTestFlag, getQueryArg } from './Helpers';

/**
 * Manages the Device identification
 *
 * @since 5.20.63
 */
export default class Device {
	/**
	 * The string representing the desktop device
	 *
	 * @since 5.0.0
	 */
	static desktop = 'desktop';

	/**
	 * The string representing the mobile device
	 *
	 * @since 5.20.63
	 */
	static mobile = 'mobile';

	/**
	 * The string representing the tablet device
	 *
	 * @since 5.20.63
	 */
	static tablet = 'tablet';

	/**
	 * The string representing the device found through parsing the request headers.
	 *
	 * @since 5.20.63
	 */
	static foundDevice = 'desktop';

	/**
	 * Identifies the device type based on either viewport width or user agent.
	 * Uses viewport width when Device.useViewport is true, otherwise uses user agent.
	 *
	 * @since 5.20.63
	 * @since 5.20.64 Added support for both viewport and user agent detection
	 * @param {boolean} useViewport Whether to use viewport width for device detection
	 *
	 * @returns {void}
	 */
	static parseDeviceInfo = (useViewport = false) => {
		const debugDevice = getTestFlag('omdevice');
		if (debugDevice) {
			Device.foundDevice = debugDevice;
			return;
		}

		if (useViewport) {
			const width =
				(window && window.innerWidth) ||
				(document && document.documentElement && document.documentElement.clientWidth) ||
				1025;
			Device.foundDevice = Device.chooseByWidth(width);
			return;
		}

		Device.foundDevice = Device.chooseByUserAgent();
	};

	/**
	 * Returns device key based on provided viewport width.
	 * Breakpoints: mobile ≤ 640, tablet 641–1024, desktop ≥ 1025
	 *
	 * @param {number} width The viewport width in pixels
	 * @returns {string} One of 'mobile' | 'tablet' | 'desktop'
	 */
	static chooseByWidth = (width) => {
		if (640 >= width) {
			return Device.mobile;
		}
		if (1024 >= width) {
			return Device.tablet;
		}
		return Device.desktop;
	};

	/**
	 * Returns device type based on user agent and platform characteristics.
	 *
	 * @since 5.20.64
	 *
	 * @returns {string} One of 'mobile' | 'tablet' | 'desktop'
	 */
	static chooseByUserAgent = () => {
		const userAgent = getQueryArg('omUserAgent') || navigator.userAgent.toLowerCase();
		let device = Device.desktop;

		// Check for tablets first
		if (/ipad|tablet|(android(?!.*mobile))|silk/i.test(userAgent)) {
			device = Device.tablet;
		}
		// Then check for mobile devices
		else if (/android.*mobile|ip(hone|od)|mobile/i.test(userAgent)) {
			device = Device.mobile;
		}

		// Special case for iPad on iOS 13+ (iPadOS)
		if (
			Device.desktop === device &&
			'MacIntel' === navigator.platform &&
			'undefined' !== typeof navigator.standalone &&
			0 < navigator.maxTouchPoints
		) {
			device = Device.tablet;
		}

		return device;
	};

	/**
	 * Returns the found device type.
	 *
	 * @since 5.20.63
	 *
	 * @returns {string} The device type.
	 */
	static get = () => {
		return Device.foundDevice || Device.desktop;
	};

	/**
	 * Checking if this device is a mobile device or not.
	 *
	 * @since 5.20.63
	 *
	 * @param   {boolean} tablets Whether or not to include tablets.
	 *
	 * @returns {boolean}         Whether or not this is a mobile device.
	 */
	static isMobile = (tablets = true) => {
		return Device.mobile === Device.get() || (tablets && Device.tablet === Device.get());
	};
}
